import React, { useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MainHeader from "../../shared/MainHeader";
import ButtonCommon from "../../shared/ButtonCommon";
import DateInput from "../../shared/DateInput";
import { POST } from "../../api/ApiService";
import { toast } from "react-toastify";
import EncryptionHelper from "../../shared/EncryptionHelper";
import { useSelector } from "react-redux";

const ActivityDashboard = () => {
  const userDetails = useSelector((state) => state.userReducer.userDetails);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  const handleRunReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end date");
      return;
    }

    setLoading(true);
    try {
      const payload = { startDate, endDate };
      const encryptData = EncryptionHelper.encryptData(payload);

      const response = await POST("daily-activity/report-dashboard", {
        encryptData,
      });

      // ✅ API already returns aggregated user-wise data
      setTableData(response || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <MainHeader headingText="Activity Dashboard" />

      <Stack
        p={3}
        gap={4}
        sx={{ border: "1px solid #C1C1C1", minHeight: "80vh" }}
      >
        {/* Info */}
        <Stack gap={1}>
          <Typography fontSize={18} fontWeight={700}>
            Social Worker Activity Analytics
          </Typography>
          <Typography fontSize={15}>
            ⓘ Select a start and end date, then click “Run Report” to view
            workload analytics.
          </Typography>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <Typography>
                Start Date <span style={{ color: "red" }}>*</span>
              </Typography>
              <DateInput
                type="date"
                value={startDate}
                onChangeValue={(e) => setStartDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography>
                End Date <span style={{ color: "red" }}>*</span>
              </Typography>
              <DateInput
                type="date"
                value={endDate}
                onChangeValue={(e) => setEndDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <ButtonCommon btnClicked={handleRunReport} disabled={loading}>
                {loading ? <CircularProgress size={22} /> : "Run Report"}
              </ButtonCommon>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Username</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Total Assigned Cases</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Total # DARs</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Total Time Billed (hrs)</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Total Admin Time (hrs)</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Total Notes Created</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : tableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  tableData.map((row) => (
                    <TableRow key={row.UserID}>
                      <TableCell>{row.Username}</TableCell>
                      <TableCell align="center">
                        {row.TotalAssignedCases}
                      </TableCell>
                      <TableCell align="center">{row.TotalDARs}</TableCell>
                      <TableCell align="center">
                        {row.TotalTimeBilled}
                      </TableCell>
                      <TableCell align="center">
                        {row.TotalAdminTimeBilled}
                      </TableCell>
                      <TableCell align="center">
                        {row.TotalNotesCreated}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Box>
  );
};

export default ActivityDashboard;
